import * as React from 'react';
import classnames from 'classnames';
import Select from 'react-select';
import { SortableContainer, arrayMove } from 'react-sortable-hoc';
import {
    ProjectLanguage,
    Question,
    MULTIMEDIA_TYPES,
    MultimediaText
} from '@nexxt/common/types';
import RichTextBubble from './RichTextBubble';
import MediaBubble from './MediaBubble';
import TextBubble from './TextBubble';
import HelperTooltip from '../../../../common/HelperTooltip';
interface MultimediaBubbleProps {
    question?: Question;
    languages?: ProjectLanguage[];
    questionText?: string;
    hideQuestionTextLabel?: boolean;
    hideMultimediaBubble?: boolean;
    disabled: boolean;
    questionError: any;
    defaultText?: any;
    helperText?: string;
    showDropdown?: boolean;
    predefinedList?: { value: string; label: string }[];
    children?: JSX.Element;
    keyName?: string;
    value?: MultimediaText[];
    disableAddBubble?: boolean;
    defaultTextType?: MULTIMEDIA_TYPES;
    settings?: {
        showTextarea?: boolean;
        noBorder?: boolean;
        isHTMLText?: boolean;
        showRootReference?: boolean;
        showDynamicReference?: boolean;
        showClose?: boolean;
        useTranslation?: boolean;
    };
    setValue?: (keyName: string, value: any) => void;
    setValueWithLanguage: (
        keyName: string,
        value: any,
        language: any,
        index: number,
        type?: MULTIMEDIA_TYPES
    ) => void;
    addNewBubble?: (
        languageCode: string,
        position: number,
        type?: MULTIMEDIA_TYPES,
        data?
    ) => void;
    removeBubble?: (languageCode: string, index: number) => void;
}

const SortableContainerWrapper = SortableContainer(({ children }) => (
    <div className="question--texts--container">{children}</div>
));

/**
 * This component provides UI for researcher to create multimedia which will
 * ultimately be rendered on chatbot as message bubble(s).
 */
const MultimediaBubble: React.FC<MultimediaBubbleProps> = ({
    question,
    languages,
    helperText,
    questionText,
    hideQuestionTextLabel,
    hideMultimediaBubble,
    disabled,
    questionError,
    defaultText,
    showDropdown,
    predefinedList,
    children,
    keyName = 'questionTexts',
    value = question?.[keyName],
    disableAddBubble,
    defaultTextType = MULTIMEDIA_TYPES.RICH_TEXT,
    settings,
    setValue,
    setValueWithLanguage,
    addNewBubble,
    removeBubble
}: MultimediaBubbleProps) => {
    const [defaultTextState, setDefaultTextState] = React.useState(defaultText);
    // hacky way to re-render components after sorting(esp. rich texts )
    const [reorderCount, setReorderCount] = React.useState(0);
    const changeDefaultTexts = (value) => {
        setDefaultTextState(value);
    };
    const onSortEnd = ({ oldIndex, newIndex }, languageCode?) => {
        const questionTexts = value;
        if (languageCode) {
            const languageIndex = questionTexts.findIndex(
                (q) => q.language === languageCode
            );
            if (languageIndex > -1) {
                const newContents = arrayMove(
                    questionTexts[languageIndex].contents,
                    oldIndex,
                    newIndex
                );
                questionTexts[languageIndex].contents = newContents;
                setValue(keyName, questionTexts);
            }
        } else {
            // for greeting text
            const newContents = arrayMove(questionTexts, oldIndex, newIndex);
            setValue(keyName, newContents);
        }
        setReorderCount((count) => count + 1);
    };

    const getDefaultContentByType = (type: MULTIMEDIA_TYPES) => {
        switch (type) {
            case MULTIMEDIA_TYPES.TEXT:
                return {
                    type: MULTIMEDIA_TYPES.TEXT,
                    text: ''
                };
            case MULTIMEDIA_TYPES.RICH_TEXT:
            default:
                return {
                    type: MULTIMEDIA_TYPES.RICH_TEXT,
                    richTexts: {}
                };
        }
    };

    const handleDeleteBubble = (languageCode: string, index: number) => {
        removeBubble(languageCode, index);
        setReorderCount((c) => c + 1);
    };
    const r = (content, language, contentIndex) => {
        switch (content?.type) {
            case MULTIMEDIA_TYPES.IMAGE:
            case MULTIMEDIA_TYPES.VIDEO:
            case MULTIMEDIA_TYPES.AUDIO:
                return (
                    <MediaBubble
                        key={`${language?.value}-${keyName}-${contentIndex}-${contentIndex}`}
                        content={content}
                        langName={language?.value || ''}
                        removeBubble={handleDeleteBubble}
                        index={contentIndex}
                        disableAddBubble={disableAddBubble}
                        disabled={disabled}
                        setValue={(value, type) =>
                            setValueWithLanguage(
                                keyName,
                                value,
                                language?.value || '',
                                contentIndex,
                                type
                            )
                        }
                    />
                );
            case MULTIMEDIA_TYPES.TEXT:
                return (
                    <TextBubble
                        content={content}
                        key={`${language?.value}-${keyName}-${contentIndex}-${contentIndex}-${reorderCount}`}
                        langName={language?.value || ''}
                        index={contentIndex}
                        setValue={(value, index) =>
                            setValueWithLanguage(
                                keyName,
                                value,
                                language?.value || '',
                                contentIndex,
                                MULTIMEDIA_TYPES.TEXT
                            )
                        }
                        settings={settings}
                        disabled={disabled}
                        disableAddBubble={disableAddBubble}
                        defaultText={defaultTextState}
                    />
                );
            case MULTIMEDIA_TYPES.RICH_TEXT:
            default:
                return (
                    <RichTextBubble
                        key={`${language?.value}-${keyName}-${contentIndex}-${contentIndex}-${reorderCount}`}
                        question={question}
                        index={contentIndex}
                        langName={language?.value || ''}
                        content={content}
                        setValue={(value, index) =>
                            setValueWithLanguage(
                                keyName,
                                value,
                                language?.value || '',
                                contentIndex
                            )
                        }
                        addNewBubble={(type, data) =>
                            addNewBubble(
                                language?.value || '',
                                contentIndex + 1,
                                type,
                                data
                            )
                        }
                        removeBubble={handleDeleteBubble}
                        defaultText={defaultTextState}
                        disabled={disabled}
                        disableAddBubble={disableAddBubble}
                        settings={settings}
                    />
                );
        }
    };

    return (
        <div className="row">
            {!hideQuestionTextLabel && (
                <>
                    <div className="col-md-12 form-group-header">
                        {helperText ? (
                            <HelperTooltip
                                placement="bottom"
                                textSize="small"
                                icon={true}
                                helperText={helperText}
                            >
                                {questionText || 'Question Text'}
                                {children || null}
                            </HelperTooltip>
                        ) : (
                            <>
                                {questionText || 'Question Text'}
                                {children || null}
                            </>
                        )}
                    </div>
                </>
            )}
            {showDropdown && predefinedList.length && (
                <div className="col-md-12 form-group form-group--inline-block">
                    <Select
                        className={'form-select--react-select react-select'}
                        classNamePrefix="react-select"
                        options={predefinedList}
                        onChange={(opt) => changeDefaultTexts(opt.value)}
                        defaultValue={predefinedList[0]}
                    />
                </div>
            )}
            {!hideMultimediaBubble && (
                <div
                    className={classnames('section-box', {
                        'no--border': settings?.noBorder
                    })}
                >
                    {languages?.length ? (
                        languages.map((language, languageIndex) => {
                            const contents = value?.find(
                                (t) => t.language == language.value
                            )?.contents || [
                                getDefaultContentByType(defaultTextType)
                            ];
                            const error =
                                questionError &&
                                questionError[keyName] &&
                                questionError[keyName][language.value];
                            return (
                                <div
                                    key={`questionTexts-${languageIndex}`}
                                    className={classnames(
                                        `col-md-12 form-group`,
                                        {
                                            error
                                        }
                                    )}
                                >
                                    <label className="form-group__label form-group__label--block">
                                        ({language?.title})
                                    </label>
                                    <SortableContainerWrapper
                                        axis="y"
                                        onSortEnd={(props) =>
                                            onSortEnd(
                                                { ...props },
                                                language.value
                                            )
                                        }
                                        onSortMove={() => {}}
                                        useDragHandle={true}
                                        helperClass="bubble--drag--container--helper"
                                    >
                                        {(contents as any[]).map(
                                            (content, contentIndex) =>
                                                r(
                                                    content,
                                                    language,
                                                    contentIndex
                                                )
                                        )}
                                    </SortableContainerWrapper>
                                    {!disableAddBubble && (
                                        <div className="rdw-editor-new-speech">
                                            <button
                                                className="btn btn--sm btn--lt-gray"
                                                disabled={disabled}
                                                onClick={(e) =>
                                                    addNewBubble(
                                                        language.value,
                                                        contents?.length
                                                    )
                                                }
                                            >
                                                <i className="nx nx-speech" />
                                                New speech bubble
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div
                            key={`questionTexts`}
                            className={`col-md-12 form-group `}
                        >
                            <label className="form-group__label form-group__label--block"></label>
                            <SortableContainerWrapper
                                axis="y"
                                onSortEnd={(props) =>
                                    onSortEnd({ ...props }, '')
                                }
                                useDragHandle={true}
                                helperClass="bubble--drag--container--helper"
                            >
                                {value?.map((content, contentIndex) =>
                                    r(content, '', contentIndex)
                                )}
                            </SortableContainerWrapper>

                            {!disableAddBubble && (
                                <div className="rdw-editor-new-speech">
                                    <button
                                        className="btn btn--sm btn--lt-gray"
                                        disabled={disabled}
                                        onClick={(e) =>
                                            addNewBubble('', value?.length)
                                        }
                                    >
                                        <i className="nx nx-speech" />
                                        New speech bubble
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MultimediaBubble;
